import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Download, Files, FileText, Clock, Search, ChevronUp, ChevronDown, HelpCircle, X, UserPlus, Plus, Sparkles, RefreshCw, CircleAlert, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAPI } from '../../services/apiService';
import { format, parseISO } from 'date-fns';
import FilterDropdown from './FilterDropdown';
import { useNavigate } from 'react-router-dom';
import DeleteCaseModal from '../DeleteCaseModal';
import MultiSelectFilterDropdown from './MultiSelectFilterDropdown';
import { useUsers } from '../../hooks/useUsers';
import { toast } from 'react-hot-toast';
import supabase from '../../services/supabaseClient';

const statusColors = {
  '-1': 'bg-red-100 text-red-800',    // error
  0: 'bg-gray-100 text-gray-800',      // draft
  1: 'bg-purple-100 text-purple-800',   // ai_review
  2: 'bg-blue-100 text-blue-800',      // human_review
  3: 'bg-yellow-100 text-yellow-800',   // qa
  4: 'bg-green-100 text-green-800',    // finished
  5: 'bg-red-100 text-red-800',        // returned
  6: 'bg-gray-100 text-gray-800'       // archived
};

const statusNames = {
  '-1': 'Error',
  0: 'Draft',
  1: 'AI Review',
  2: 'Human Review',
  3: 'QA',
  4: 'Finished',
  5: 'Returned',
  6: 'Archived'
};

const statusDescriptions = {
  '-1': 'Failed during AI processing',
  0: 'Not yet submitted for review',
  1: 'Being processed by Paribus AI',
  2: 'In review by a human analyst',
  3: 'In QA by a human analyst',
  4: 'Awaiting for manager approval',
  5: 'Sent back to the physician',
  6: 'Archived by the physician'
};

const filterTypes = {
  status: { label: 'Status', field: 'status_id' },
  physician: { label: 'Physician', field: 'physician' },
  analyst: { label: 'Analyst', field: 'analyst' }
};

const AIReviewBadge = ({ startTime }) => {
  return (
    <div className="relative p-[2px] rounded-full bg-gradient-to-r from-purple-800/50 via-transparent via-transparent to-purple-800/50 bg-[length:400%_100%] animate-[gradient-x_6s_linear_infinite] min-w-[120px]">
      <div className="relative inline-flex items-center px-3 py-2 rounded-full text-xs font-medium bg-purple-100 text-purple-800 w-full">
        <Sparkles className="h-4 w-4 mr-2" />
        <span>AI Review</span>
      </div>
    </div>
  );
};

const StatusBadge = ({ status_id, onStatusChange, canChangeStatus, startTime }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { hasPermission } = useAuth();
  
  // Don't allow status changes during AI Review unless super admin
  const isEditable = canChangeStatus && (status_id !== 1 || hasPermission('super_admin')); 
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStatusClick = (e) => {
    if (!isEditable) return;
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleStatusChange = (newStatus, e) => {
    e.stopPropagation();
    setIsOpen(false);
    onStatusChange(newStatus);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {status_id === 1 ? (
        <div 
          className={`${isEditable ? 'cursor-pointer hover:opacity-80' : ''}`}
          onClick={handleStatusClick}
        >
          <AIReviewBadge startTime={startTime} />
        </div>
      ) : (
        <div 
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
            ${statusColors[status_id] || 'bg-gray-100'}
            ${isEditable ? 'cursor-pointer hover:opacity-80' : ''}`}
          onClick={handleStatusClick}
        >
          <span>{statusNames[status_id] || 'Unknown'}</span>
          {isEditable && (
            <ChevronDown className="ml-1 h-3 w-3" />
          )}
        </div>
      )}

      {isOpen && isEditable && (
        <div className="fixed mt-1 py-1 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50" style={{ top: dropdownRef.current?.getBoundingClientRect().bottom + 4, left: dropdownRef.current?.getBoundingClientRect().left }}>
          <div className="py-1" role="menu">
            {Object.entries(statusNames)
              .filter(([id]) => {
                const numId = Number(id);
                // Only super admins can see AI Review in dropdown
                if (numId === 1 && !hasPermission('super_admin')) return false;
                // Only users with specific permissions can move to certain statuses
                if (numId === 4 && !hasPermission('case_actions', 'finalize')) return false;
                if (numId === 3 && !hasPermission('case_actions', 'mark_reviewed')) return false;
                return true;
              })
              .map(([id, name]) => (
                <button
                  key={id}
                  className={`w-full px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2
                    ${Number(id) === status_id ? 'bg-gray-50' : ''}`}
                  onClick={(e) => handleStatusChange(Number(id), e)}
                >
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[id]}`}>
                    {name}
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PhysicianSelect = ({ caseId, currentPhysicianId, onUpdate, fetchCases }) => {
  const api = useAPI();
  const [physicians, setPhysicians] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPhysicians = async () => {
      try {
        const { data } = await api.get('/physicians');
        setPhysicians(data || []);
      } catch (error) {
        console.error('Error fetching physicians:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPhysicians();
  }, []);

  const handleChange = async (e) => {
    e.stopPropagation();
    try {
      const newPhysicianId = e.target.value;
      const { data } = await api.put(`/cases/${caseId}/physician`, {
        user_id: newPhysicianId
      });
      if (data) {
        onUpdate({
          physician_id: data.physician_id,
          physician: data.physician
        });
      }
    } catch (error) {
      console.error('Error updating physician:', error);
      fetchCases();
    }
  };

  if (loading) {
    return (
      <select disabled className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
        <option>Loading...</option>
      </select>
    );
  }

  return (
    <select
      value={currentPhysicianId || ''}
      onChange={handleChange}
      onClick={e => e.stopPropagation()}
      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
    >
      <option value="">Select Physician</option>
      {physicians.map(physician => (
        <option key={physician.user_id} value={physician.user_id}>
          {physician.name}
        </option>
      ))}
    </select>
  );
};

const AnalystBadges = ({ caseId, analysts, onUpdate }) => {
  const api = useAPI();
  const { users } = useUsers();
  const availableAnalysts = users.filter(user => user.role_name === 'analyst');
  const [showAddModal, setShowAddModal] = useState(false);

  const handleAddAnalyst = async (userId) => {
    try {
      await api.post(`/cases/${caseId}/analysts`, {
        user_id: userId
      });
      setShowAddModal(false);
      onUpdate();
    } catch (error) {
      console.error('Error adding analyst:', error);
    }
  };

  const handleRemoveAnalyst = async (userId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/cases/${caseId}/analysts/${userId}`);
      onUpdate();
    } catch (error) {
      console.error('Error removing analyst:', error);
    }
  };

  return (
    <div className="flex flex-col gap-1" onClick={e => e.stopPropagation()}>
      {analysts.map(analyst => (
        <span
          key={analyst.user_id}
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 w-fit"
        >
          {analyst.name}
          <X
            className="ml-1.5 h-3 w-3 cursor-pointer hover:text-blue-600"
            onClick={(e) => handleRemoveAnalyst(analyst.user_id, e)}
          />
        </span>
      ))}
      
      <button
        onClick={() => setShowAddModal(true)}
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 w-fit"
      >
        <UserPlus className="h-3 w-3" />
      </button>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Add Analyst</h3>
            <div className="flex flex-col space-y-2">
              {availableAnalysts
                .filter(a => !analysts.find(existing => existing.user_id === a.user_id))
                .map(analyst => (
                  <button
                    key={analyst.user_id}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-md border border-gray-200"
                    onClick={() => handleAddAnalyst(analyst.user_id)}
                  >
                    {analyst.name}
                  </button>
                ))}
            </div>
            <button
              className="mt-6 text-sm text-gray-600 hover:text-gray-800"
              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const PreviewCaseModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-medium mb-4">Preview Chronology</h3>
        <p className="text-gray-600 mb-6">
          This chronology has yet to be fully reviewed by our team, so we cannot guarantee 100% accuracy. Would you like to preview the chronology?
        </p>
        <div className="flex justify-end space-x-4">
          <button
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            onClick={onConfirm}
          >
            Preview
          </button>
        </div>
      </div>
    </div>
  );
};

const CaseManagement = () => {
  const [statusTabs, setStatusTabs] = useState([
    { name: 'Active', value: 'active', count: 0 },
    { name: 'Finished', value: 'finished', count: 0 },
    { name: 'Returned', value: 'returned', count: 0 },
    { name: 'Archived', value: 'staged', count: 0 }
  ]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCases, setSelectedCases] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewCaseId, setPreviewCaseId] = useState(null);
  const navigate = useNavigate();
  const [activeFilters, setActiveFilters] = useState({});
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [currentFilterType, setCurrentFilterType] = useState(null);
  const { hasPermission, sessionStatus, user, firmContext } = useAuth();
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [showDeleteCaseModal, setShowDeleteCaseModal] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);
  const { users } = useUsers();
  const api = useAPI();
  const [error, setError] = useState(null);

  // Add polling interval state
  const [pollingInterval, setPollingInterval] = useState(null);

  // Add a ref to track initial load
  const isInitialLoad = useRef(true);

  // Add state for error modal
  const [selectedErrors, setSelectedErrors] = useState(null);

  const isProfessional = () => {
    return firmContext?.roleName === 'professional';
  };

  const getSortableFields = () => {
    const baseFields = {
      'patient': 'Patient',
      'exam_date': 'Exam Date',
      'files': 'Files',
      'pages': 'Pages'
    };

    if (!isProfessional()) {
      return {
        ...baseFields,
        'physician': 'Physician',
        'analyst': 'Analyst',
      };
    }

    return baseFields;
  };

  const sortableFields = getSortableFields();

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedCases = (cases) => {
    return [...cases].sort((a, b) => {
      // First sort by user-selected field if exists
      if (sortField) {
        const aVal = a[sortField];
        const bVal = b[sortField];
        
        if (sortField === 'exam_date') {
          const dateA = aVal ? new Date(aVal) : new Date(0);
          const dateB = bVal ? new Date(bVal) : new Date(0);
          return sortDirection === 'asc' 
            ? dateA - dateB 
            : dateB - dateA;
        }

        if (aVal === bVal) {
          // If values are equal, sort by status_id as secondary sort
          return b.status_id - a.status_id;
        }

        // Handle null/undefined values
        if (!aVal) return sortDirection === 'asc' ? -1 : 1;
        if (!bVal) return sortDirection === 'asc' ? 1 : -1;

        return sortDirection === 'asc'
          ? aVal.toString().localeCompare(bVal.toString())
          : bVal.toString().localeCompare(aVal.toString());
      }

      // Default sort by status_id if no field selected
      return b.status_id - a.status_id;
    });
  };

  // Add visibility handling
  useEffect(() => {
    fetchCases();

    const interval = setInterval(() => {
      // Only poll if the page is visible
      if (!document.hidden) {
        fetchCases();
      }
    }, 20000);

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Fetch immediately when tab becomes visible
        fetchCases();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Clean up
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Modify fetchCases to handle loading state properly
  const fetchCases = async () => {
    // Only show loading on initial fetch
    if (isInitialLoad.current) {
      setLoading(true);
    }

    try {
      const { data } = await api.get('/cases');
      
      // Only update state if there are actual changes
      if (JSON.stringify(data) !== JSON.stringify(cases)) {
        setCases(data);
      }
    } catch (error) {
      console.error('Error fetching cases:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      if (error.message === 'Session expired') {
        return; // API service will handle redirect
      }
      
      // Only show error toast on initial load
      if (cases.length === 0) {
        toast.error('Failed to load cases. Please try refreshing the page.');
      }
    } finally {
      if (isInitialLoad.current) {
        setLoading(false);
        isInitialLoad.current = false;
      }
    }
  };

  useEffect(() => {
    if (sessionStatus === 'expired') {
      navigate('/login');
    } else if (sessionStatus === 'error') {
      toast.error('Having trouble connecting to the server. Will retry shortly...');
    }
  }, [sessionStatus, navigate]);

  const isActiveStatus = (status_id) => {
    // Error (-1) is not considered active, and active statuses are 0-4
    return status_id >= 0 && status_id <= 4;
  };

  const getTabCounts = (cases) => {
    const counts = {
      active: cases.filter(c => isActiveStatus(c.status_id)).length,
      finished: cases.filter(c => c.status_id === 4).length,
      returned: cases.filter(c => c.status_id === 5).length,
      staged: cases.filter(c => c.status_id === 6).length
    };
    
    setStatusTabs(prevTabs => 
      prevTabs.map(tab => ({
        ...tab,
        count: counts[tab.value] || 0
      }))
    );
  };

  useEffect(() => {
    if (cases.length > 0) {
      getTabCounts(cases);
    }
  }, [cases]);

  const getFilterOptions = (filterType) => {
    if (!cases.length) return [];
    
    const field = filterTypes[filterType].field;
    if (filterType === 'status') {
      return Object.keys(statusNames).map(id => statusNames[id]);
    }
    
    return [...new Set(cases.map(c => c[field]).filter(Boolean))];
  };

  const getFilteredAndSearchedCases = () => {
    let filtered = [...cases];
    
    // Apply all active filters
    Object.entries(activeFilters).forEach(([filterType, selectedValues]) => {
      if (selectedValues.length > 0) {
        filtered = filtered.filter(item => {
          const field = filterTypes[filterType].field;
          if (filterType === 'status') {
            const statusName = statusNames[item.status_id];
            return selectedValues.includes(statusName);
          }
          return selectedValues.includes(item[field]);
        });
      }
    });
    
    // Apply search query
    if (searchQuery) {
      filtered = filtered.filter(item => 
        Object.values(item).some(val => 
          val?.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }
    
    return getSortedCases(filtered);
  };

  const handleRowClick = (case_id) => {
    // Handle row click to view case details
  };

  const handleFilesClick = (case_id, e) => {
    e.stopPropagation();
    navigate(`/cases/${case_id}/files`);
  };

  const handleChronologyClick = (case_id, e) => {
    e.stopPropagation();
    const caseItem = cases.find(c => c.case_id === case_id);
    
    if (isProfessional() && caseItem && caseItem.status_id !== 5 && caseItem.status_id !== 6) {
      setPreviewCaseId(case_id);
      setShowPreviewModal(true);
    } else {
      navigate(`/cases/${case_id}/chronology`);
    }
  };

  const handleDeleteClick = (case_id) => {
    setCaseToDelete(case_id);
    setShowDeleteCaseModal(true);
  };

  const confirmDeleteCase = async () => {
    try {
      const { error } = await api.delete(`/cases/${caseToDelete}`);

      if (error) throw error;

      // Update local state to remove the deleted case
      setCases(prevCases => prevCases.filter(c => c.case_id !== caseToDelete));
      setShowDeleteCaseModal(false);
      setCaseToDelete(null);
    } catch (error) {
      console.error('Error deleting case:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleFilterSelect = (filterType) => {
    setCurrentFilterType(filterType);
    setFilterMenuOpen(true);
  };

  const handleFilterValueChange = (value) => {
    setActiveFilters(prev => {
      const currentValues = prev[currentFilterType] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      
      return {
        ...prev,
        [currentFilterType]: newValues
      };
    });
  };

  const removeFilter = (filterType, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].filter(v => v !== value)
    }));
  };

  const clearAllFilters = () => {
    setActiveFilters({});
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleDownloadClick = (case_id) => {
    // Handle download click
  };

  const canDeleteCase = () => {
    return hasPermission('case_actions', 'delete') || hasPermission('super_admin');
  };

  const canChangeStatus = () => {
    // Check for either update_status permission or super_admin
    return hasPermission('case_actions', 'update_status') || hasPermission('super_admin');
  };

  const handleStatusChange = async (caseId, newStatus) => {
    try {
      // Check if case is in AI Review and user is not super admin
      const caseItem = cases.find(c => c.case_id === caseId);
      if (caseItem?.status_id === 1 && !hasPermission('super_admin')) {
        toast.error('Only super admins can move cases out of AI Review');
        return;
      }

      // Additional permission checks for specific statuses
      if (newStatus === 4 && !hasPermission('case_actions', 'finalize')) {
        toast.error('You do not have permission to finalize cases');
        return;
      }

      if (newStatus === 3 && !hasPermission('case_actions', 'mark_reviewed')) {
        toast.error('You do not have permission to mark cases as reviewed');
        return;
      }

      await api.put(`/cases/${caseId}/status`, { status_id: newStatus });
      setCases(prevCases => 
        prevCases.map(c => 
          c.case_id === caseId ? { 
            ...c, 
            status_id: newStatus,
            ai_review_start_time: newStatus === 1 ? new Date().toISOString() : c.ai_review_start_time
          } : c
        )
      );
    } catch (error) {
      console.error('Error updating case status:', error);
      toast.error('Failed to update case status');
    }
  };

  const SortableHeader = ({ field, label, width = "w-48" }) => (
    <th 
      scope="col" 
      className={`${width} px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50 cursor-pointer group sticky top-0`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
          <ChevronUp 
            className={`h-3 w-3 ${sortField === field && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-400'}`}
          />
          <ChevronDown 
            className={`h-3 w-3 -mt-1 ${sortField === field && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-400'}`}
          />
        </div>
      </div>
    </th>
  );

  // Add ref for the filter container
  const filterRef = useRef(null);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setFilterMenuOpen(false);
        setCurrentFilterType(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handlePhysicianChange = async (caseId, newPhysicianId) => {
    try {
      const response = await api.put(`/cases/${caseId}/physician`, {
        user_id: newPhysicianId
      });
      return response.data;
    } catch (error) {
      console.error('Error updating physician:', error);
      throw error;
    }
  };

  const handleAnalystChange = async (caseId, analystId) => {
    try {
      const response = await api.put(`/cases/${caseId}/analyst`, {
        user_id: analystId
      });
      return response.data;
    } catch (error) {
      console.error('Error updating analyst:', error);
      throw error;
    }
  };

  // Add error boundary for component
  useEffect(() => {
    const handleError = (event) => {
      console.error('Unhandled error:', event.reason);
      toast.error('Something went wrong. Please try refreshing the page.');
    };
    
    window.addEventListener('unhandledrejection', handleError);
    return () => window.removeEventListener('unhandledrejection', handleError);
  }, []);

  const handleNewCase = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }
    if (!firmContext) {
      console.error('No firm context available');
      toast.error('Please select a firm before creating a case');
      return;
    }
    try {
      setLoading(true);
      
      // Check current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session. User is not authenticated.');
      }
      console.log('Active session found. User ID:', session.user.id);

      console.log('Creating new case for User ID:', user.id, 'Firm ID:', firmContext.firmId);
      const { data: caseData, error: caseError } = await supabase
        .from('cases')
        .insert([{ 
          case_name: 'New Case', 
          user_id: user.id,
          firm_id: firmContext.firmId 
        }])
        .select();

      if (caseError) throw caseError;

      const newCaseId = caseData[0].case_id;
      setCases(prevCases => [...prevCases, caseData[0]]);
      navigate(`/cases/${newCaseId}/files`);
    } catch (error) {
      console.error('Error creating new case:', error);
      setError(`Error creating new case: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewConfirm = () => {
    setShowPreviewModal(false);
    if (previewCaseId) {
      navigate(`/cases/${previewCaseId}/chronology`);
    }
    setPreviewCaseId(null);
  };

  const handleRerunChronology = async (caseId) => {
    try {
      // First resolve all errors for this case
      await api.put(`/cases/${caseId}/resolve-errors`);

      // Then make the med_chron request
      const response = await api.post('/med_chron/request', {
        case_id: caseId
      });

      if (response.data.status === 'success') {
        // Update case status to AI Review (1)
        await handleStatusChange(caseId, 1);
        
        // Update local state to remove errors
        setCases(prevCases => 
          prevCases.map(c => 
            c.case_id === caseId 
              ? { ...c, unresolved_errors: [] }
              : c
          )
        );
        
        toast.success('Chronology re-run started');
      } else {
        throw new Error('Failed to submit request');
      }
    } catch (error) {
      if (error.message === 'Session expired') {
        return; // API service will handle redirect
      }
      console.error('Error re-running chronology:', error);
      toast.error('Failed to re-run chronology');
    }
  };

  useEffect(() => {
    const handleScrollAndResize = () => {
      const tableContainer = document.querySelector('.overflow-x-auto');
      const fadeElement = document.querySelector('.table-fade');
      
      if (tableContainer && fadeElement) {
        // Check if there's overflow and not at the end
        const hasOverflow = tableContainer.scrollWidth > tableContainer.clientWidth;
        const isAtEnd = tableContainer.scrollLeft + tableContainer.clientWidth >= tableContainer.scrollWidth - 10;
        
        // Only show fade if there's overflow and not at the end
        fadeElement.style.opacity = (hasOverflow && !isAtEnd) ? '1' : '0';
      }
    };

    const tableContainer = document.querySelector('.overflow-x-auto');
    if (tableContainer) {
      tableContainer.addEventListener('scroll', handleScrollAndResize);
      window.addEventListener('resize', handleScrollAndResize);
      // Initial check
      handleScrollAndResize();
    }

    // Cleanup
    return () => {
      const tableContainer = document.querySelector('.overflow-x-auto');
      if (tableContainer) {
        tableContainer.removeEventListener('scroll', handleScrollAndResize);
        window.removeEventListener('resize', handleScrollAndResize);
      }
    };
  }, [cases]); // Re-run when cases change as this might affect table width

  const canResolveErrors = () => {
    return hasPermission('case_actions', 'resolve_error') || hasPermission('super_admin');
  };

  const handleResolveAllErrors = async (caseId, e) => {
    e.stopPropagation();
    try {
      await api.put(`/cases/${caseId}/resolve-errors`);
      
      // Update local state to remove errors
      setCases(prevCases => 
        prevCases.map(c => 
          c.case_id === caseId 
            ? { ...c, unresolved_errors: [] }
            : c
        )
      );
      
      toast.success('All errors resolved');
    } catch (error) {
      console.error('Error resolving errors:', error);
      toast.error('Failed to resolve errors');
    }
  };

  // Add error modal close handler
  const handleCloseErrorModal = () => {
    setSelectedErrors(null);
  };

  return (
    <div className="container mx-auto px-2 py-6">
      <div className="bg-white rounded-lg">
        <div className="px-6 pt-6 pb-4 bg-[#F9FAFB]">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="relative" ref={filterRef}>
                  <button
                    onClick={() => setFilterMenuOpen(!filterMenuOpen)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <span>Filter by...</span>
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </button>

                  {filterMenuOpen && (
                    <div className="absolute left-0 mt-1 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                      <div className="py-1" role="menu">
                        {Object.entries(filterTypes).map(([key, { label }]) => (
                          <button
                            key={key}
                            className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFilterSelect(key);
                            }}
                          >
                            <span>{label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {currentFilterType && (
                    <MultiSelectFilterDropdown
                      isOpen={currentFilterType !== null}
                      onClose={() => setCurrentFilterType(null)}
                      options={getFilterOptions(currentFilterType)}
                      selectedValues={activeFilters[currentFilterType] || []}
                      onSelectionChange={handleFilterValueChange}
                      filterType={currentFilterType}
                    />
                  )}
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search"
                    className="pl-10 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
              </div>
              
              <button
                onClick={handleNewCase}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-blue-800 bg-blue-200 hover:bg-blue-300 transition-colors"
              >
                <Plus size={16} className="mr-2" /> New Case
              </button>
            </div>

            {/* Active filters display */}
            {Object.keys(activeFilters).length > 0 && (
              <div className="flex items-center space-x-2">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(activeFilters).map(([filterType, values]) =>
                    values.map((value) => (
                      <span
                        key={`${filterType}-${value}`}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        {filterTypes[filterType].label}: {value}
                        <X
                          className="ml-2 h-4 w-4 cursor-pointer hover:text-blue-600"
                          onClick={() => removeFilter(filterType, value)}
                        />
                      </span>
                    ))
                  )}
                  <button
                    className="text-sm text-gray-500 hover:text-gray-700"
                    onClick={clearAllFilters}
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="relative">
          <div className="relative">
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-20 table-fade opacity-0 transition-opacity duration-200 hidden md:block" />
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow relative">
              <div className="min-w-[1024px]">
                <table className="w-full table-fixed">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="border-b border-gray-200">
                      <th scope="col" className="w-12 px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50 sticky top-0">#</th>
                      <th scope="col" className="w-8 px-2 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50 sticky top-0">
                        <div className="relative group">
                          <div className="invisible group-hover:visible absolute left-1/2 transform -translate-x-1/2 mt-2 w-48 px-4 py-3 bg-gray-900 text-white text-xs rounded-lg z-10">
                            Indicates unresolved errors
                            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 border-8 border-transparent border-b-gray-900"></div>
                          </div>
                        </div>
                      </th>
                      <SortableHeader field="patient" label="Patient" width="w-48" />
                      <th scope="col" className="w-48 px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50 sticky top-0">Actions</th>
                      <th scope="col" className="w-40 px-8 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50 sticky top-0">
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          <div className="relative group">
                            <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                            <div className="invisible group-hover:visible absolute left-1/2 transform -translate-x-1/2 mt-2 w-64 px-4 py-3 bg-gray-900 text-white text-xs rounded-lg z-10">
                              <div className="space-y-2">
                                {Object.entries(statusDescriptions).map(([id, desc]) => (
                                  <div key={id} className="flex flex-col">
                                    <span className="font-semibold">{statusNames[id]}:</span>
                                    <span className="text-gray-300">{desc}</span>
                                  </div>
                                ))}
                              </div>
                              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 border-8 border-transparent border-b-gray-900"></div>
                            </div>
                          </div>
                        </div>
                      </th>
                      {!isProfessional() && (
                        <>
                          <SortableHeader field="physician" label="Physician" width="w-48" />
                          <SortableHeader field="analyst" label="Analyst" width="w-48" />
                        </>
                      )}
                      <SortableHeader field="exam_date" label="Exam Date" width="w-28" />
                      <SortableHeader field="files" label="Files" width="w-20" />
                      <SortableHeader field="pages" label="Pages" width="w-20" />
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={isProfessional() ? "7" : "9"} className="px-6 py-4 text-center">Loading...</td>
                      </tr>
                    ) : (
                      getFilteredAndSearchedCases().map((caseItem, index) => (
                        <tr 
                          key={caseItem.case_id} 
                          className="hover:bg-gray-50 cursor-pointer border-t border-gray-200"
                          onClick={() => handleRowClick(caseItem.case_id)}
                        >
                          <td className="px-6 py-4 text-sm text-gray-500">{index + 1}</td>
                          <td className="px-2 py-4 text-center">
                            {caseItem.unresolved_errors?.length > 0 && (
                              <div className="relative group">
                                <button 
                                  className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center hover:bg-red-200 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedErrors(caseItem.unresolved_errors);
                                  }}
                                >
                                  <CircleAlert className="w-4 h-4 text-red-600" />
                                </button>
                                <div className="invisible group-hover:visible absolute left-1/2 -translate-x-1/2 -translate-y-2 bottom-full mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded-md whitespace-nowrap">
                                  Click for details
                                  <div className="absolute left-1/2 -translate-x-1/2 translate-y-full bottom-0 border-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors duration-150 break-words">
                              {caseItem.patient}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap h-[52px]">
                            <div className="grid grid-cols-6 gap-4 h-full items-center justify-items-center">
                              {/* View Chronology */}
                              <div className="w-5 flex items-center justify-center">
                                {caseItem.status_id !== 0 && caseItem.status_id !== 1 ? (
                                  <button 
                                    className="text-gray-400 hover:text-blue-600 group relative flex items-center justify-center"
                                    onClick={(e) => handleChronologyClick(caseItem.case_id, e)}
                                  >
                                    <FileText className="h-5 w-5" />
                                    <span className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded z-50">
                                      View Chronology
                                    </span>
                                  </button>
                                ) : <div className="w-5" />}
                              </div>

                              {/* Rerun Chronology */}
                              <div className="w-5 flex items-center justify-center">
                                {[2, 3, 4, -1].includes(caseItem.status_id) ? (
                                  <button 
                                    className="text-gray-400 hover:text-blue-600 group relative flex items-center justify-center"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRerunChronology(caseItem.case_id);
                                    }}
                                  >
                                    <RefreshCw className="h-5 w-5" />
                                    <span className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded z-50">
                                      Re-run Chronology
                                    </span>
                                  </button>
                                ) : <div className="w-5" />}
                              </div>

                              {/* View Files */}
                              <div className="w-5 flex items-center justify-center">
                                <button 
                                  className="text-gray-400 hover:text-blue-600 group relative flex items-center justify-center"
                                  onClick={(e) => handleFilesClick(caseItem.case_id, e)}
                                >
                                  <Files className="h-5 w-5" />
                                  <span className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded z-50">
                                    View Files
                                  </span>
                                </button>
                              </div>

                              {/* Delete Case */}
                              <div className="w-5 flex items-center justify-center">
                                {canDeleteCase() ? (
                                  <button 
                                    className="text-gray-400 hover:text-red-600 group relative flex items-center justify-center"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(caseItem.case_id);
                                    }}
                                  >
                                    <Trash2 className="h-5 w-5" />
                                    <span className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded z-50">
                                      Delete Case
                                    </span>
                                  </button>
                                ) : <div className="w-5" />}
                              </div>

                              {/* Download Case */}
                              <div className="w-5 flex items-center justify-center">
                                <button 
                                  className="text-gray-400 hover:text-blue-600 group relative flex items-center justify-center"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadClick(caseItem.case_id);
                                  }}
                                >
                                  <Download className="h-5 w-5" />
                                  <span className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded z-50">
                                    Download Case
                                  </span>
                                </button>
                              </div>

                              {/* Resolve All Errors */}
                              <div className="w-5 flex items-center justify-center">
                                {canResolveErrors() && caseItem.unresolved_errors?.length > 0 ? (
                                  <button 
                                    className="text-gray-400 hover:text-green-600 group relative flex items-center justify-center"
                                    onClick={(e) => handleResolveAllErrors(caseItem.case_id, e)}
                                  >
                                    <Check className="h-5 w-5" />
                                    <span className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded z-50">
                                      Resolve All Errors
                                    </span>
                                  </button>
                                ) : <div className="w-5" />}
                              </div>
                            </div>
                          </td>
                          <td className="px-8 py-4 whitespace-nowrap">
                            <StatusBadge 
                              status_id={caseItem.status_id} 
                              canChangeStatus={canChangeStatus()}
                              onStatusChange={(newStatus) => handleStatusChange(caseItem.case_id, newStatus)}
                              startTime={caseItem.ai_review_start_time}
                            />
                          </td>
                          {!isProfessional() && (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <PhysicianSelect
                                  caseId={caseItem.case_id}
                                  currentPhysicianId={caseItem.physician_id}
                                  fetchCases={fetchCases}
                                  onUpdate={(physicianData) => {
                                    setCases(prevCases => 
                                      prevCases.map(c => 
                                        c.case_id === caseItem.case_id 
                                          ? { 
                                              ...c, 
                                              physician_id: physicianData.physician_id,
                                              physician: physicianData.physician
                                            }
                                          : c
                                      )
                                    );
                                  }}
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <AnalystBadges
                                  caseId={caseItem.case_id}
                                  analysts={caseItem.analysts || []}
                                  onUpdate={() => fetchCases()}
                                />
                              </td>
                            </>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {caseItem.exam_date ? caseItem.exam_date.split('T')[0].split('-').map((part, i) => 
                              i === 1 ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][parseInt(part) - 1] + ' ' + parseInt(caseItem.exam_date.split('T')[0].split('-')[2]) :
                              i === 2 ? ', ' + caseItem.exam_date.split('T')[0].split('-')[0] :
                              ''
                            ).join('') : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{caseItem.files || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{caseItem.pages || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Error Modal */}
      {selectedErrors && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleCloseErrorModal}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 transform" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Case Errors</h3>
              <button 
                className="text-gray-400 hover:text-gray-500"
                onClick={handleCloseErrorModal}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-6 max-h-[70vh] overflow-y-auto">
              {selectedErrors.map((error, i) => (
                <div 
                  key={i} 
                  className="p-4 bg-red-50 rounded-lg border border-red-200"
                >
                  <div className="flex items-start space-x-3">
                    <CircleAlert className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-red-900">{error.description}</p>
                      {error.error_details?.message && (
                        <p className="mt-2 text-red-700">{error.error_details.message}</p>
                      )}
                      {error.error_details?.recommended_action && (
                        <div className="mt-3 p-3 bg-red-50/50 rounded-lg border border-red-100">
                          <div className="flex items-center gap-2 mb-2">
                            <span role="img" aria-label="lightbulb" className="text-lg">💡</span>
                            <p className="font-bold text-red-900">How to fix</p>
                          </div>
                          <p className="text-red-800">{error.error_details.recommended_action}</p>
                        </div>
                      )}
                      <p className="mt-2 text-sm text-gray-500">
                        {format(parseISO(error.created_at), 'MMM d, yyyy HH:mm:ss')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <DeleteCaseModal
        isOpen={showDeleteCaseModal}
        onClose={() => {
          setShowDeleteCaseModal(false);
          setCaseToDelete(null);
        }}
        onConfirm={confirmDeleteCase}
      />

      <PreviewCaseModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewCaseId(null);
        }}
        onConfirm={handlePreviewConfirm}
      />
    </div>
  );
};

export default CaseManagement; 